// Normalized image coordinates form the future detector's input contract.
// No photographs, measurements, cookies or credentials leave this component.
(() => {
  const names = ['Outer left', 'Inner left', 'Inner right', 'Outer right', 'Outer top', 'Inner top', 'Inner bottom', 'Outer bottom'];
  const initial = () => [.08, .13, .87, .92, .07, .12, .88, .93];
  // Checked 2026-10-13. These are photo-side centering references, not grades.
  // PSA: live grade-definition slides at psacard.com/gradingstandards.
  // CGC lower numeric limits explicitly apply to sports/non-sports, not TCG.
  // Beckett: historical official acceleratorManual.pdf; current site unavailable.
  const references = (lr, tb, side) => {
    const horizontal = Math.max(lr, 100 - lr), vertical = Math.max(tb, 100 - tb);
    const worst = Math.max(horizontal, vertical), best = Math.min(horizontal, vertical);
    const at = limit => worst <= limit + 1e-9;
    const psa = side === 'front'
      ? at(55) ? '10' : at(60) ? '9' : at(65) ? '8' : at(70) ? '7' : at(80) ? '6' : at(85) ? '5' : at(90) ? '3' : '<3'
      : at(75) ? '10' : at(90) ? '9' : '<3';
    const cgc = at(side === 'front' ? 55 : 75) ? '10' : at(side === 'front' ? 60 : 90) ? '9' : side === 'front' && at(65) ? '8' : side === 'front' ? '<8' : '<9';
    const bgs = side === 'front'
      ? at(50) ? '10' : best <= 50 + 1e-9 && at(55) ? '9.5' : at(55) ? '9' : at(60) ? '8' : '<8'
      : at(60) ? '10' : at(70) ? '9' : at(80) ? '8' : '<8';
    return [
      { score: psa, note: 'Published centering reference; highest matching band for this photo' },
      { score: cgc, note: cgc === '10' ? 'Gem Mint centering reference' : 'Sports/non-sports reference; not a published TCG rule' },
      { score: bgs, note: 'Historical reference; current rules pending verification' },
    ];
  };
  const svgNS = 'http://www.w3.org/2000/svg';
  const svgNode = (tag, attrs) => {
    const node = document.createElementNS(svgNS, tag);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
    return node;
  };
  if (customElements.get('bp-centering')) return;
  customElements.define('bp-centering', class extends HTMLElement {
    connectedCallback() {
      this.inspector = this.parentElement.querySelector('bp-image-magnifier');
      this.imageBox = this.inspector?.querySelector('.bp-inspect-image');
      this.photo = this.imageBox?.querySelector('img');
      if (!this.photo) return;
      this.events = new AbortController();
      const on = (node, type, fn, capture = false) => node.addEventListener(type, fn, { signal: this.events.signal, capture });
      this.positions = initial();
      this.active = false;
      this.edited = false; this.zoom = 1; this.detectionAttempted = false;
      this.toggle = this.querySelector('[data-centering-toggle]');
      this.panel = this.querySelector('[data-centering-panel]');
      this.panel.dataset.side = this.dataset.side;
      this.launch = document.createElement('div'); this.launch.className = 'bp-centering-launch';
      this.help = this.inspector.querySelector('[data-bp-inspect-help]');
      this.helpHome = this.help.parentElement;
      this.launch.append(this.help, this.toggle);
      this.side = this.querySelector('[data-centering-side]');
      this.side.value = ['front', 'back'].includes(this.dataset.side) ? this.dataset.side : '';
      this.status = this.querySelector('[data-centering-status]');
      this.ratios = this.querySelector('[data-centering-ratios]');
      this.overlay = svgNode('svg', { viewBox: '0 0 1000 1000', preserveAspectRatio: 'none', class: 'bp-centering-overlay', 'aria-label': 'Adjust outer and inner card borders' });
      this.overlay.hidden = true;
      // SVG does not expose HTMLElement.hidden consistently across browsers.
      this.overlay.setAttribute('hidden', '');
      const patternId = this.panel.id + '-hatch';
      const defs = svgNode('defs', {}), pattern = svgNode('pattern', { id: patternId, width: 12, height: 12, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' });
      pattern.append(svgNode('line', { x1: 0, y1: 0, x2: 0, y2: 12, stroke: '#39ff14', 'stroke-width': 1 })); defs.append(pattern); this.overlay.append(defs);
      this.borderBands = Array.from({ length: 4 }, (_, i) => {
        const rect = svgNode('rect', { class: 'bp-centering-band', fill: `url(#${patternId})`, 'data-border': ['left', 'right', 'top', 'bottom'][i], 'aria-hidden': 'true' });
        this.overlay.append(rect); return rect;
      });
      this.inspector.classList.add('bp-centering-photo-aligned');
      this.imageBox.classList.add('bp-centering-image');
      this.imageBox.append(this.overlay);
      this.readout = document.createElement('div'); this.readout.className = 'bp-centering-readout'; this.readout.hidden = true;
      this.liveRatios = document.createElement('div'); this.liveRatios.className = 'bp-centering-ratios';
      this.ratioValues = ['L/R', 'T/B'].map(name => { const cell = document.createElement('div'); const label = document.createElement('span'); label.textContent = name; const value = document.createElement('strong'); cell.append(label, value); this.liveRatios.append(cell); return value; });
      const title = document.createElement('div'); title.className = 'bp-centering-score-title'; title.textContent = 'Centering references · this photo only'; this.readout.append(title); this.scoreTitle = title;
      const metrics = document.createElement('div'); metrics.className = 'bp-centering-metrics'; this.readout.append(metrics);
      const scores = document.createElement('div'); scores.className = 'bp-centering-scores'; metrics.append(scores);
      this.scoreRows = ['PSA', 'CGC', 'Beckett'].map(name => {
        const row = document.createElement('div'); row.className = 'bp-centering-score';
        const label = document.createElement('span'); label.textContent = name;
        const score = document.createElement('strong'); score.dataset.centeringScore = name; score.textContent = '—';
        const note = document.createElement('small'); note.textContent = 'Move guides to begin';
        row.append(label, score, note); scores.append(row); return { score, note };
      });
      metrics.append(this.liveRatios);
      const actions = document.createElement('div'); actions.className = 'bp-centering-actions';
      this.zoomButton = document.createElement('button'); this.zoomButton.type = 'button'; this.zoomButton.className = 'bp-inspect-action'; this.zoomButton.textContent = '4×'; this.zoomButton.setAttribute('aria-label', 'Zoom 4×'); this.zoomButton.setAttribute('aria-pressed', 'false');
      this.zoomButton.dataset.centeringIcon = 'zoom';
      actions.append(this.zoomButton, this.panel);
      const header = document.createElement('div'); header.className = 'bp-centering-header'; header.append(title, actions); this.readout.prepend(header);
      this.autoNote = document.createElement('div'); this.autoNote.className = 'bp-centering-auto-note'; this.autoNote.setAttribute('role', 'status');
      this.readout.append(this.autoNote);
      this.inspector.prepend(this.launch, this.readout);
      this.viewport = document.createElement('div'); this.viewport.className = 'bp-centering-viewport';
      this.imageBox.before(this.viewport); this.viewport.append(this.imageBox);
      on(this.zoomButton, 'click', () => this.setZoom(this.zoom === 1 ? 4 : 1));
      this.photo.draggable = false;
      on(this.viewport, 'pointerdown', event => {
        if (!this.active || this.zoom === 1 || event.button !== 0 || event.isPrimary === false || this.drag || this.pan || event.target.closest('.bp-centering-handle')) return;
        event.preventDefault();
        this.pan = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, top: this.viewport.scrollTop };
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.classList.add('bp-centering-panning');
      });
      on(this.viewport, 'pointermove', event => {
        if (this.pan?.id !== event.pointerId) return;
        event.preventDefault();
        this.viewport.scrollLeft = this.pan.left - (event.clientX - this.pan.x);
        this.viewport.scrollTop = this.pan.top - (event.clientY - this.pan.y);
      });
      const stopPan = event => { if (this.pan?.id === event.pointerId) this.stopPan(); };
      on(this.viewport, 'pointerup', stopPan); on(this.viewport, 'pointercancel', stopPan); on(this.viewport, 'lostpointercapture', stopPan);
      this.handles = [];
      names.forEach((name, i) => {
        const axis = i < 4 ? 'x' : 'y', inner = [1, 2, 5, 6].includes(i);
        const handle = svgNode('g', { class: 'bp-centering-handle', tabindex: '0', role: 'slider', 'aria-label': name, 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-orientation': axis === 'x' ? 'horizontal' : 'vertical', 'data-axis': axis });
        handle.append(svgNode('line', { class: 'bp-centering-line', stroke: inner ? '#ff00d4' : '#39ff14', 'stroke-width': '.75', 'stroke-dasharray': inner ? '2 3' : 'none', 'vector-effect': 'non-scaling-stroke' }));
        // Transparent hit area remains wide in screen pixels on a narrow phone.
        handle.append(svgNode('line', { stroke: 'transparent', 'stroke-width': '24', 'vector-effect': 'non-scaling-stroke' }));
        handle.append(svgNode('rect', { class: 'bp-centering-knob', fill: inner ? '#ff00d4' : '#39ff14', stroke: '#101820', 'stroke-width': '.75', 'vector-effect': 'non-scaling-stroke' }));
        this.overlay.append(handle); this.handles.push(handle);
        on(handle, 'focus', () => { this.selectedBorder = i < 4 ? (i < 2 ? 0 : 1) : (i < 6 ? 2 : 3); this.paint(); });
        on(handle, 'pointerdown', event => {
          if (event.button !== 0 || event.isPrimary === false || this.drag || this.pan) return;
          event.preventDefault(); event.stopPropagation(); handle.focus({ preventScroll: true });
          const box = this.overlay.getBoundingClientRect();
          const cursor = axis === 'x' ? (event.clientX - box.left) / box.width : (event.clientY - box.top) / box.height;
          this.drag = { index: i, id: event.pointerId, handle, offset: this.positions[i] - cursor };
          handle.setPointerCapture(event.pointerId);
          this.place(event, i);
        });
        on(handle, 'pointermove', event => { if (this.drag?.id === event.pointerId) this.place(event, this.drag.index); });
        const end = event => { if (this.drag?.id === event.pointerId) this.drag = null; };
        on(handle, 'pointerup', end); on(handle, 'pointercancel', end); on(handle, 'lostpointercapture', end);
        on(handle, 'keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const direction = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
          const pixels = axis === 'x' ? this.photo.naturalWidth : this.photo.naturalHeight;
          this.move(i, this.positions[i] + direction * (event.shiftKey ? 10 : 1) / (pixels || 1000));
        });
      });
      this.info = this.panel.querySelector('.bp-centering-info');
      on(this.readout, 'keydown', event => {
        if (event.key === 'Escape' && this.info.open) { this.info.open = false; this.info.querySelector('summary').focus(); }
      });
      on(document, 'pointerdown', event => { if (this.info.open && !this.info.contains(event.target)) this.info.open = false; }, true);
      on(this.toggle, 'click', () => this.setActive(!this.active));
      on(this.side, 'change', () => this.paint());
      on(this.photo, 'load', () => { this.toggle.hidden = false; this.sizeZoom(); this.paint(); if (this.active && !this.detectionAttempted) this.detectGuides(); });
      on(this.photo, 'error', () => { this.setActive(false); this.toggle.hidden = true; });
      this.toggle.hidden = !this.photo.complete || !this.photo.naturalWidth;
      this.resize = new ResizeObserver(() => { this.sizeZoom(); this.paint(); }); this.resize.observe(this.viewport); this.resize.observe(this.photo);
      this.paint();
    }
    disconnectedCallback() {
      this.detection?.abort();
      this.events?.abort(); this.resize?.disconnect(); this.overlay?.remove(); this.helpHome?.prepend(this.help); this.append(this.toggle, this.panel); this.launch?.remove(); this.readout?.remove();
      this.inspector?.classList.remove('bp-centering-active', 'bp-centering-photo-aligned');
      this.inspector?.style.removeProperty('--bp-photo-inset');
      if (this.viewport?.parentNode) { this.viewport.before(this.imageBox); this.viewport.remove(); this.imageBox.style.width = ''; }

    }
    setActive(active) {
      this.stopPan();
      if (this.drag?.handle.hasPointerCapture(this.drag.id)) this.drag.handle.releasePointerCapture(this.drag.id);
      this.active = active; this.drag = null; this.inspector.stop?.();
      this.inspector.classList.toggle('bp-centering-active', active);
      this.toggle.setAttribute('aria-pressed', String(active));
      this.toggle.querySelector('[data-centering-toggle-label]').textContent = active ? 'Hide centering' : 'Show centering';
      this.panel.hidden = !active;
      this.readout.hidden = !active;
      this.overlay.toggleAttribute('hidden', !active);
      if (!active) { this.info.open = false; this.setZoom(1); }
      this.sizeZoom(); this.paint();
      if (active && this.photo.naturalWidth && !this.detectionAttempted) this.detectGuides();
    }
    sizeZoom() {
      this.viewport.classList.toggle('bp-centering-viewport--zoomed', this.active && this.zoom > 1);
      this.imageBox.style.width = this.active && this.zoom > 1 ? (this.viewport.clientWidth * this.zoom) + 'px' : '';
      // object-fit: contain leaves horizontal space inside the img element.
      // Align the controls with the rendered photo, not that element's box.
      const photoBox = this.photo.getBoundingClientRect();
      const visibleWidth = this.photo.naturalHeight ? Math.min(photoBox.width, photoBox.height * this.photo.naturalWidth / this.photo.naturalHeight) : photoBox.width;
      const inset = this.active ? 0 : Math.max(0, (this.inspector.clientWidth - visibleWidth) / 2);
      this.inspector.style.setProperty('--bp-photo-inset', inset + 'px');
    }
    setZoom(scale) {
      this.stopPan();
      const box = this.viewport;
      const centerX = (box.scrollLeft + box.clientWidth / 2) / (this.imageBox.clientWidth || 1);
      const centerY = (box.scrollTop + box.clientHeight / 2) / (this.imageBox.clientHeight || 1);
      this.zoom = scale; this.zoomButton.textContent = scale === 1 ? '4×' : '1×'; this.zoomButton.setAttribute('aria-label', scale === 1 ? 'Zoom 4×' : 'Fit photo (1×)'); this.zoomButton.setAttribute('aria-pressed', String(scale > 1)); this.sizeZoom();
      if (scale > 1) { box.scrollLeft = centerX * this.imageBox.clientWidth - box.clientWidth / 2; box.scrollTop = centerY * this.imageBox.clientHeight - box.clientHeight / 2; }
      else { box.scrollLeft = 0; box.scrollTop = 0; }
      this.paint();
    }
    stopPan() {
      const pointer = this.pan; this.pan = null;
      this.viewport?.classList.remove('bp-centering-panning');
      if (pointer && this.viewport.hasPointerCapture(pointer.id)) this.viewport.releasePointerCapture(pointer.id);
    }
    place(event, i) {
      const box = this.overlay.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const cursor = i < 4 ? (event.clientX - box.left) / box.width : (event.clientY - box.top) / box.height;
      this.move(i, cursor + (this.drag?.offset || 0));
    }
    move(i, value) {
      this.autoNote.textContent = '';
      this.detectionAttempted = true;
      this.detection?.abort();
      const local = i % 4;
      const low = local ? this.positions[i - 1] + .0001 : 0;
      const high = local < 3 ? this.positions[i + 1] - .0001 : 1;
      this.positions[i] = Math.max(low, Math.min(high, value)); this.edited = true; this.paint();
    }
    async detectGuides() {
      this.detection?.abort();
      const controller = new AbortController(); this.detection = controller;
      // Measure the full inspection photograph, not a small responsive image.
      const source = this.inspector.dataset.imageUrl || this.photo.currentSrc || this.photo.src;
      this.detectionAttempted = true;
      this.autoNote.textContent = 'Detecting borders…';
      try {
        if (!globalThis.BPCenteringDetector) throw new Error('unavailable');
        const response = await fetch(source, { mode: 'cors', credentials: 'omit', signal: controller.signal });
        if (!response.ok) throw new Error('image');
        const localURL = URL.createObjectURL(await response.blob());
        const image = new Image(); image.src = localURL;
        let pixels;
        try {
          await image.decode();
          const scale = Math.min(1, 1200 / Math.max(image.naturalWidth, image.naturalHeight));
          const canvas = document.createElement('canvas'); canvas.width = Math.round(image.naturalWidth * scale); canvas.height = Math.round(image.naturalHeight * scale);
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, canvas.width, canvas.height); pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        } finally { URL.revokeObjectURL(localURL); }
        if (controller.signal.aborted || !this.isConnected || source !== (this.inspector.dataset.imageUrl || this.photo.currentSrc || this.photo.src)) return;
        const result = globalThis.BPCenteringDetector.detect(pixels);
        if (!result.ok) { this.autoNote.textContent = 'Border unclear — adjust the guides manually.'; return; }
        this.positions = [...result.guides]; this.edited = true; this.paint();
        this.autoNote.textContent = '';
      } catch (error) {
        if (!controller.signal.aborted) this.autoNote.textContent = 'Photo could not be measured — adjust the guides manually.';
      }
    }
    paint() {
      const box = this.imageBox.getBoundingClientRect();
      const knobW = 8 / Math.max(1, box.width) * 1000, knobH = 8 / Math.max(1, box.height) * 1000;
      this.handles.forEach((handle, i) => {
        const value = this.positions[i] * 1000, vertical = i < 4;
        handle.querySelectorAll('line').forEach(line => {
          const attrs = vertical ? { x1: value, x2: value, y1: 0, y2: 1000 } : { x1: 0, x2: 1000, y1: value, y2: value };
          Object.entries(attrs).forEach(([key, v]) => line.setAttribute(key, v));
        });
        // Stagger paired handles so thin borders remain independently selectable.
        const stagger = [0, 3].includes(i % 4) ? 420 : 580;
        const knob = handle.querySelector('rect');
        knob.setAttribute('x', (vertical ? value : stagger) - knobW / 2);
        knob.setAttribute('y', (vertical ? stagger : value) - knobH / 2);
        knob.setAttribute('width', knobW); knob.setAttribute('height', knobH);
        const percent = (this.positions[i] * 100).toFixed(2);
        handle.setAttribute('aria-valuenow', percent); handle.setAttribute('aria-valuetext', percent + ' percent of photo');
      });
      const p = this.positions, left = p[1] - p[0], right = p[3] - p[2], top = p[5] - p[4], bottom = p[7] - p[6];
      const bands = [[p[0], p[4], left, p[7] - p[4]], [p[2], p[4], right, p[7] - p[4]], [p[1], p[4], p[2] - p[1], top], [p[1], p[6], p[2] - p[1], bottom]];
      this.borderBands.forEach((rect, i) => { ['x', 'y', 'width', 'height'].forEach((key, j) => rect.setAttribute(key, bands[i][j] * 1000)); rect.dataset.selected = String(this.selectedBorder === i); });
      const lr = 100 * left / (left + right), tb = 100 * top / (top + bottom);
      this.ratios.textContent = `Left/right ${lr.toFixed(1)}/${(100 - lr).toFixed(1)} · Top/bottom ${tb.toFixed(1)}/${(100 - tb).toFixed(1)}`;
      this.ratioValues[0].textContent = `${lr.toFixed(1)} | ${(100 - lr).toFixed(1)}`;
      this.ratioValues[1].textContent = `${tb.toFixed(1)} | ${(100 - tb).toFixed(1)}`;
      const ready = this.edited;
      this.scoreTitle.textContent = 'Centering · ' + (this.side.value || 'choose side');
      const results = ready && this.side.value ? references(lr, tb, this.side.value) : null;
      this.scoreRows.forEach((row, i) => {
        row.score.textContent = results ? results[i].score : '—';
        row.score.classList.toggle('bp-centering-ten', results?.[i].score === '10');
        row.note.textContent = results ? results[i].note : ready ? 'Choose photo side' : 'Move guides to begin';
        row.score.parentElement.title = row.note.textContent;
      });
      this.status.textContent = this.side.value ? '' : 'Choose front or back to compare this photo.';
    }
  });
})();
